import React from "react";
import { SearchFilters } from "../filters/filterTypes";
import PhotoPermissionsDropdown from "../filters/PhotoPermissionsDropdown";
import DateRangeDropdown from "../filters/DateRangeDropdown";
import EventFilterDropdown from "../filters/EventFilterDropdown";
import TagFilterDropdown from "../filters/TagFilterDropdown";
import PeopleFilterDropdown from "../filters/PeopleFilterDropdown";
import SelfieSearchDropdown from "../filters/SelfieSearchDropdown";
import LoadingSpinner from "./LoadingSpinner";
import greySelfieIcon from "../style/grey-selfie.svg";
import { formatDateRangeDisplay, PhotoDateRange } from "../utils/dateRangeUtils";
import { ActiveDropdown } from "./types";

interface FiltersBarProps {
  filters: SearchFilters;
  activeDropdown: ActiveDropdown;
  toggleDropdown: (name: ActiveDropdown) => void;
  applyFilter: (
    key: keyof SearchFilters,
    value: any,
    options?: {
      filterName?: ActiveDropdown;
      itemId?: number;
      permissionKey?: string;
    }
  ) => void;
  availablePeople: Array<{ id: number; name: string; photo_count: number }>;
  availableEvents: Array<{ id: number; name: string; photo_count: number }>;
  availableTags: Array<{ id: number; name: string; photo_count: number }>;
  availablePermissionAttributes: Array<{
    attributeId: number;
    attributeLabel: string;
  }>;
  showPermissionsFilter: boolean;
  showPeopleFilter: boolean;
  hasRedactedParticipants: boolean;
  isLoading: boolean;
  loadingItemId: number | null;
  loadingPermissionKey: string | null;
  loadingFilter: ActiveDropdown;
  peopleActive: boolean;
  permissionsActive: boolean;
  dateActive: boolean;
  selfieActive: boolean;
  eventActive: boolean;
  tagActive: boolean;
  selfieSearchS3Bucket: string;
  photoDateRange: PhotoDateRange | null;
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  filters,
  activeDropdown,
  toggleDropdown,
  applyFilter,
  availablePeople,
  availableEvents,
  availableTags,
  availablePermissionAttributes,
  showPermissionsFilter,
  showPeopleFilter,
  hasRedactedParticipants,
  isLoading,
  loadingItemId,
  loadingPermissionKey,
  loadingFilter,
  peopleActive,
  permissionsActive,
  dateActive,
  selfieActive,
  eventActive,
  tagActive,
  selfieSearchS3Bucket,
  photoDateRange
}) => {
  const dateLabel = formatDateRangeDisplay(
    filters.dateRange.startDate,
    filters.dateRange.endDate
  ) || "Photo taken date";

  return (
    <div className="filters-desktop-container">
      {showPeopleFilter && (
      <div
        className={`filter-item${
          peopleActive ? " filter-item--active" : ""
        }`}
      >
        <button
          type="button"
          className="filter-item__toggle"
          onClick={() => toggleDropdown("people")}
          aria-label={`Filter by people${
            peopleActive
              ? ` (${filters.selectedPersonIds.length} selected)`
              : ""
          }`}
          aria-expanded={activeDropdown === "people"}
        >
          <span className="filter-item__label">
            People
            {peopleActive && (
              <span className="filter-item__badge">
                {filters.selectedPersonIds.length}
              </span>
            )}
          </span>
          <i className="fa fa-angle-down filter-item__icon"></i>
        </button>
        {activeDropdown === "people" && (
          <PeopleFilterDropdown
            people={availablePeople}
            selectedPersonIds={filters.selectedPersonIds}
            onChange={(v, clickedId) =>
              applyFilter("selectedPersonIds", v, {
                filterName: "people",
                itemId: clickedId
              })
            }
            onClose={() => toggleDropdown(null)}
            isLoading={isLoading}
            loadingItemId={loadingItemId}
          />
        )}
      </div>
      )}

      {showPermissionsFilter && (
      <div
        className={`filter-item${
          permissionsActive ? " filter-item--active" : ""
        }`}
      >
        <button
          type="button"
          className="filter-item__toggle"
          onClick={() => toggleDropdown("permissions")}
          aria-label="Filter by photo permissions"
          aria-expanded={activeDropdown === "permissions"}
        >
          <span className="filter-item__label">
            {isLoading && loadingFilter === "permissions" && (
              <LoadingSpinner position="before" />
            )}
            Photo permissions
            {permissionsActive && (
              <span className="filter-item__badge">
                {filters.photoPermissions.permissions.filter(
                  p => p.value !== "all"
                ).length +
                  (filters.photoPermissions.redactedPhoto !== "all"
                    ? 1
                    : 0)}
              </span>
            )}
          </span>
          <i className="fa fa-angle-down filter-item__icon"></i>
        </button>
        {activeDropdown === "permissions" && (
          <PhotoPermissionsDropdown
            value={filters.photoPermissions}
            onChange={(v, key) =>
              applyFilter("photoPermissions", v, {
                filterName: "permissions",
                permissionKey: key
              })
            }
            onClose={() => toggleDropdown(null)}
            availableAttributes={availablePermissionAttributes}
            showRedacted={hasRedactedParticipants}
            isLoading={isLoading}
            loadingKey={loadingPermissionKey}
          />
        )}
      </div>
      )}

      <div
        className={`filter-item${dateActive ? " filter-item--active" : ""}`}
      >
        <button
          type="button"
          className="filter-item__toggle"
          onClick={() => toggleDropdown("date")}
          aria-label="Filter by date range"
          aria-expanded={activeDropdown === "date"}
        >
          <span className="filter-item__label">
            {isLoading && loadingFilter === "date" && (
              <LoadingSpinner position="before" />
            )}
            {dateLabel}
          </span>
          <i className="fa fa-angle-down filter-item__icon"></i>
        </button>
        {activeDropdown === "date" && (
          <DateRangeDropdown
            value={filters.dateRange}
            onChange={v =>
              applyFilter("dateRange", v, { filterName: "date" })
            }
            onClose={() => toggleDropdown(null)}
            photoDateRange={photoDateRange}
          />
        )}
      </div>

      <div
        className={`filter-item${
          selfieActive ? " filter-item--active" : ""
        }`}
      >
        <button
          type="button"
          className="filter-item__toggle"
          onClick={() => toggleDropdown("selfie")}
          aria-label="Filter by selfie search"
          aria-expanded={activeDropdown === "selfie"}
        >
          <span className="filter-item__label">
            {isLoading && loadingFilter === "selfie" && (
              <LoadingSpinner position="before" />
            )}
            {selfieActive && filters.selfie.imageUrl ? (
              <img
                src={filters.selfie.imageUrl}
                alt="Selfie preview"
                className="selfie-filter-preview"
              />
            ) : (
              <img
                src={greySelfieIcon}
                alt=""
                width="14"
                height="14"
                style={{ marginRight: "4px" }}
              />
            )}
            {selfieActive ? "Change Selfie" : "Selfie search"}
          </span>
        </button>
        {activeDropdown === "selfie" && (
          <SelfieSearchDropdown
            value={filters.selfie}
            onChange={v => {
              applyFilter("selfie", v, { filterName: "selfie" });
              toggleDropdown(null);
            }}
            onClose={() => toggleDropdown(null)}
            selfieSearchS3Bucket={selfieSearchS3Bucket}
          />
        )}
      </div>

      <div
        className={`filter-item${
          eventActive ? " filter-item--active" : ""
        }`}
      >
        <button
          type="button"
          className="filter-item__toggle"
          onClick={() => toggleDropdown("event")}
          aria-label={`Filter by event${
            eventActive
              ? ` (${filters.selectedEventIds.length} selected)`
              : ""
          }`}
          aria-expanded={activeDropdown === "event"}
        >
          <span className="filter-item__label">
            Select event
            {eventActive && (
              <span className="filter-item__badge">
                {filters.selectedEventIds.length}
              </span>
            )}
          </span>
          <i className="fa fa-angle-down filter-item__icon"></i>
        </button>
        {activeDropdown === "event" && (
          <EventFilterDropdown
            events={availableEvents}
            selectedEventIds={filters.selectedEventIds}
            onChange={(v, clickedId) =>
              applyFilter("selectedEventIds", v, {
                filterName: "event",
                itemId: clickedId
              })
            }
            onClose={() => toggleDropdown(null)}
            isLoading={isLoading}
            loadingItemId={loadingItemId}
          />
        )}
      </div>

      <div
        className={`filter-item${tagActive ? " filter-item--active" : ""}`}
      >
        <button
          type="button"
          className="filter-item__toggle"
          onClick={() => toggleDropdown("tags")}
          aria-label={`Filter by tags${
            tagActive ? ` (${filters.selectedTagIds.length} selected)` : ""
          }`}
          aria-expanded={activeDropdown === "tags"}
        >
          <span className="filter-item__label">
            Tags
            {tagActive && (
              <span className="filter-item__badge">
                {filters.selectedTagIds.length}
              </span>
            )}
          </span>
          <i className="fa fa-angle-down filter-item__icon"></i>
        </button>
        {activeDropdown === "tags" && (
          <TagFilterDropdown
            tags={availableTags}
            selectedTagIds={filters.selectedTagIds}
            onChange={(v, clickedId) =>
              applyFilter("selectedTagIds", v, {
                filterName: "tags",
                itemId: clickedId
              })
            }
            onClose={() => toggleDropdown(null)}
            isLoading={isLoading}
            loadingItemId={loadingItemId}
          />
        )}
      </div>
    </div>
  );
};

export default FiltersBar;
